/**
 * CategoryManager - Componente Principal de Gestão de Categorias
 * Interface completa para gestão de categorias de tags com analytics e operações CRUD
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../contexts/ThemeContext.tsx';
import {
  Folder,
  Plus,
  Search,
  Filter,
  BarChart3,
  Hash,
  Users,
  TrendingUp,
  Edit,
  Trash2,
  RefreshCw,
  CheckCircle,
  X
} from 'lucide-react';

import {
  CategoriesService,
  TagsService, // Added import
  CategoryResponse,
  CategoryCreate,
  CategoryUpdate
} from '../services/apiTags.ts';
import CategoryModal from './CategoryModal.tsx';

interface CategoryManagerProps {
  clinicId: number;
  className?: string;
}

const CategoryManager: React.FC<CategoryManagerProps> = ({
  clinicId,
  className = ''
}) => {
  const { isDark } = useTheme();
  // Estado principal
  const [categories, setCategories] = useState<CategoryResponse[]>([]);
  const [allTags, setAllTags] = useState<any[]>([]); // Estado para armazenar todas as tags
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Estado da UI
  const [searchQuery, setSearchQuery] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  // Modal
  const [modalCategory, setModalCategory] = useState<CategoryResponse | undefined>();
  const [showModal, setShowModal] = useState(false);

  // Analytics
  const [analytics, setAnalytics] = useState({
    totalCategories: 0,
    totalTags: 0,
    activeCategories: 0,
    inactiveCategories: 0,
    averageTagsPerCategory: 0
  });

  // Carregar categorias e tags
  const loadCategories = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [categoriesData, tagsData] = await Promise.all([
        CategoriesService.getCategoriesWithCount(clinicId),
        TagsService.getTags(clinicId, { limit: 1000 }) // Carregar todas as tags
      ]);

      // Filtrar categoria "Automática" (ID 0) e "general" se for automática
      const visibleCategories = categoriesData.filter(cat =>
        cat.id !== 0 && cat.name.toLowerCase() !== 'automática'
      );

      setCategories(visibleCategories);
      setAllTags(tagsData);

      // Calcular analytics usando dados reais
      const totalTags = tagsData.length;
      const activeCategories = visibleCategories.length;

      setAnalytics({
        totalCategories: visibleCategories.length,
        totalTags: totalTags,
        activeCategories: activeCategories,
        inactiveCategories: 0,
        averageTagsPerCategory: activeCategories > 0 ? Math.round(totalTags / activeCategories) : 0
      });

      console.log(`✅ ${visibleCategories.length} categorias e ${tagsData.length} tags carregadas`);
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || 'Erro ao carregar dados';
      setError(errorMessage);
      console.error('❌ Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  }, [clinicId]);

  // Carregar dados iniciais
  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  // Filtrar categorias baseado na busca
  const filteredCategories = React.useMemo(() => {
    let filtered = [...categories];

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(category =>
        category.name.toLowerCase().includes(query) ||
        category.description?.toLowerCase().includes(query)
      );
    }

    return filtered.sort((a, b) => {
      // IDs virtuais (automáticos) vêm no final
      if (a.id === 0 && b.id !== 0) return 1;
      if (b.id === 0 && a.id !== 0) return -1;
      return a.name.localeCompare(b.name);
    });
  }, [categories, searchQuery]);

  // Abrir modal para criar/editar categoria
  const handleOpenModal = (category?: CategoryResponse) => {
    setModalCategory(category);
    setShowModal(true);
  };

  // Fechar modal
  const handleCloseModal = () => {
    setShowModal(false);
    setModalCategory(undefined);
  };

  // Salvar categoria (criar/editar)
  const handleSaveCategory = async () => {
    await loadCategories();
    handleCloseModal();
  };

  // Criar nova categoria rapidamente
  const handleCreateCategory = async (categoryName: string): Promise<CategoryResponse> => {
    const categoryData: CategoryCreate = {
      name: categoryName,
      description: '',
      color: CategoriesService.generateRandomCategoryColor()
    };

    return await CategoriesService.createCategory(clinicId, categoryData);
  };

  // Deletar categoria
  const handleDeleteCategory = async (category: CategoryResponse) => {
    if (category.id === 0) {
      alert('Categorias automáticas não podem ser excluídas');
      return;
    }

    if (!window.confirm(`Tem certeza que deseja excluir a categoria "${category.name}"?`)) {
      return;
    }

    try {
      await CategoriesService.deleteCategory(category.id);
      await loadCategories();
      console.log(`✅ Categoria "${category.name}" excluída`);
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || 'Erro ao excluir categoria';
      setError(errorMessage);
      console.error('❌ Erro ao excluir categoria:', error);
    }
  };

  // Renderizar analytics
  const renderAnalytics = () => {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className={`border rounded-xl p-4 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-slate-200'}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>Total de Categorias</p>
              <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{analytics.totalCategories}</p>
            </div>
            <div className={`p-2 rounded-lg ${isDark ? 'bg-sminy-primary/20 text-sminy-primary' : 'bg-sminy-primary/10 text-sminy-primary'}`}>
              <Folder className="w-6 h-6" />
            </div>
          </div>
        </div>

        <div className={`border rounded-xl p-4 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-slate-200'}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>Total de Tags</p>
              <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{analytics.totalTags}</p>
            </div>
            <div className={`p-2 rounded-lg ${isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-50 text-blue-600'}`}>
              <Hash className="w-6 h-6" />
            </div>
          </div>
        </div>

        <div className={`border rounded-xl p-4 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-slate-200'}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>Categorias Ativas</p>
              <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{analytics.activeCategories}</p>
            </div>
            <div className={`p-2 rounded-lg ${isDark ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-50 text-emerald-600'}`}>
              <CheckCircle className="w-6 h-6" />
            </div>
          </div>
        </div>

      </div>
    );
  };

  // Renderizar lista de categorias
  const renderCategoryList = () => {
    return (
      <div className="space-y-4">
        {filteredCategories.map(category => {
          // Filtrar tags desta categoria
          const categoryTags = allTags.filter(tag =>
            (tag.category || 'general').toLowerCase() === category.name.toLowerCase()
          );

          return (
            <div
              key={category.id}
              className={`flex flex-col p-4 border rounded-xl transition-colors ${isDark
                ? 'bg-gray-800 border-gray-700 hover:border-gray-600'
                : 'bg-white border-slate-200 hover:border-slate-300'
                }`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div
                    className="w-6 h-6 rounded-full flex-shrink-0 shadow-sm"
                    style={{ backgroundColor: category.color }}
                  />
                  <div>
                    <div className="flex items-center gap-2">
                      <p className={`font-medium ${isDark ? 'text-gray-200' : 'text-slate-900'}`}>
                        {category.name}
                      </p>
                    </div>
                    {category.description && (
                      <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>{category.description}</p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleOpenModal(category)}
                    className={`p-2 rounded-lg transition-colors ${isDark
                      ? 'text-gray-400 hover:text-sminy-primary hover:bg-gray-700'
                      : 'text-slate-400 hover:text-sminy-primary hover:bg-slate-100'
                      }`}
                    title="Editar categoria"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteCategory(category)}
                    className={`p-2 rounded-lg transition-colors ${isDark
                      ? 'text-gray-400 hover:text-red-400 hover:bg-gray-700'
                      : 'text-slate-400 hover:text-red-600 hover:bg-slate-100'
                      }`}
                    title="Excluir categoria"
                    disabled={categoryTags.length > 0}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Lista de Tags da Categoria */}
              <div className="pl-9">
                {categoryTags.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {categoryTags.map(tag => (
                      <div
                        key={tag.id}
                        className="inline-flex items-center px-2 py-1 rounded text-xs font-medium"
                        style={{
                          backgroundColor: `${tag.color}20`, // 20% opacity
                          color: tag.color,
                          border: `1px solid ${tag.color}40`
                        }}
                      >
                        <Hash className="w-3 h-3 mr-1" />
                        {tag.name}
                        <span className={`ml-1 text-[10px] ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
                          ({tag.usage_count})
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className={`text-xs italic ${isDark ? 'text-gray-600' : 'text-slate-400'}`}>Nenhuma tag nesta categoria</p>
                )}
              </div>
            </div>
          );
        })}

        {filteredCategories.length === 0 && (
          <div className={`text-center py-12 ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>
            <Folder className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-gray-700' : 'text-slate-300'}`} />
            <p className="font-medium">Nenhuma categoria encontrada</p>
            <p className="text-sm mt-1">Crie sua primeira categoria para começar</p>
          </div>
        )}
      </div>
    );
  };

  // Renderizar topo com controles
  const renderHeader = () => {
    return (
      <div className="flex flex-col xl:flex-row items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3 flex-1 w-full xl:w-auto">
          <div className={`flex items-center gap-3 w-full xl:w-auto p-1.5 rounded-xl transition-all ${isDark ? 'bg-gray-800' : 'bg-white border border-slate-200'
            }`}>
            <div className="relative flex-1 xl:w-64">
              <Search className={`w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-slate-400'}`} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar categorias..."
                className={`w-full pl-9 pr-4 py-1.5 text-sm bg-transparent border-none outline-none ${isDark ? 'text-white placeholder-gray-500' : 'text-slate-900 placeholder-slate-400'
                  }`}
              />
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          <button
            onClick={() => handleOpenModal()}
            className="flex items-center gap-2 px-4 py-2 bg-sminy-primary text-white rounded-xl hover:bg-sminy-primary-hover transition-colors shadow-sm shadow-sminy-primary/20"
          >
            <Plus className="w-4 h-4" />
            Nova Categoria
          </button>

          <button
            onClick={loadCategories}
            className={`p-2 rounded-xl transition-colors ${isDark
              ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
              : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
              }`}
            title="Recarregar"
            disabled={loading}
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {renderHeader()}

      {/* Analytics */}
      {renderAnalytics()}

      {/* Filtros e Busca */}
      {/* Filtros e Busca removidos daqui pois foram movidos para o header */}

      {/* Conteúdo principal */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-sminy-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : error ? (
        <div className={`border rounded-lg p-4 ${isDark ? 'bg-red-900/20 border-red-800 text-red-400' : 'bg-red-50 border-red-200 text-red-600'}`}>
          ⚠️ {error}
        </div>
      ) : (
        renderCategoryList()
      )}

      {/* Modal */}
      <CategoryModal
        category={modalCategory}
        isOpen={showModal}
        onClose={handleCloseModal}
        onSave={handleSaveCategory}
        clinicId={clinicId}
        onCreateCategory={handleCreateCategory}
      />
    </div>
  );
};

export default CategoryManager;